import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy } from 'lucide-react';
import { fileUrl, LiveListEntry } from '../../services/tournamentOpService';

/** Bild-URL eines Live-Eintrags: zentraler Helper (leer → null, data:/http direkt, sonst /api/file). */
const liveImageUrl = (image?: string | null): string | null => fileUrl(image);

/**
 * Sidebar-Widget: Community-Turniere, die gerade über die
 * DartCloud-Turniersoftware laufen. Kompakte Zeilen → /live/:operationId.
 * Blendet sich aus, wenn nichts läuft.
 */
const SidebarLiveTournaments: React.FC<{ entries: LiveListEntry[] }> = ({ entries }) => {
  if (entries.length === 0) return null;

  return (
    <section className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900">
      <div className="flex items-center gap-2 border-b border-zinc-800 px-4 py-3">
        <h2 className="section-heading text-base font-bold text-white">Live-Turniere</h2>
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
        </span>
        <Link
          to="/spiele"
          className="ml-auto shrink-0 text-xs font-semibold text-[#3fced1] transition-colors hover:text-white"
        >
          Alle anzeigen
        </Link>
      </div>
      <div>
        {entries.map((entry) => (
          <LiveRow key={entry.operationId} entry={entry} />
        ))}
      </div>
    </section>
  );
};

const LiveRow: React.FC<{ entry: LiveListEntry }> = ({ entry }) => {
  const [imageError, setImageError] = useState(false);
  const imageUrl = liveImageUrl(entry.image);
  const showImage = imageUrl && !imageError;
  // Competition-Betrieb: Competition-Name als Titel, Hauptturnier als Zusatz.
  // Defensiv — ältere Backends liefern die Felder nicht.
  const displayName = entry.competitionName || entry.name;
  const parentName = entry.competitionName ? entry.parentTournamentName ?? null : null;

  return (
    <Link
      to={`/live/${entry.operationId}`}
      className="flex items-center gap-3 border-b border-zinc-800 px-4 py-3 transition-colors last:border-b-0 hover:bg-zinc-800/40"
    >
      {showImage ? (
        <img
          src={imageUrl as string}
          alt=""
          loading="lazy"
          onError={() => setImageError(true)}
          className="h-10 w-10 shrink-0 rounded-lg object-cover"
        />
      ) : (
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-zinc-800">
          <Trophy size={16} className="text-zinc-500" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <h3 className="truncate text-sm font-semibold text-white">{displayName}</h3>
        <p className="mt-0.5 truncate text-xs text-gray-500">
          {parentName ? `${parentName} · ` : ''}
          {entry.liveMatches === 1 ? '1 Spiel live' : `${entry.liveMatches} Spiele live`}
          {' · '}
          {entry.participants} Teilnehmer
        </p>
      </div>
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-500">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
        Live
      </span>
    </Link>
  );
};

export default SidebarLiveTournaments;
